import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';

const buttonVariants = cva(
  [
    'inline-flex shrink-0 cursor-pointer select-none items-center justify-center gap-2',
    'whitespace-nowrap rounded-[10px] transition-colors duration-150',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      appearance: {
        accent: [
          'bg-v2-btn-primary text-v2-btn-primary-text',
          'hover:bg-v2-btn-primary-hover hover:text-v2-btn-primary-text-2',
        ].join(' '),
        neutral: [
          'bg-v2-btn-secondary text-v2-btn-secondary-text',
          'hover:bg-v2-btn-secondary-hover',
        ].join(' '),
        outline: [
          'border border-v2-btn-secondary-hover bg-transparent text-v2-btn-secondary-text',
          'hover:border-v2-btn-primary hover:text-v2-btn-primary',
        ].join(' '),
        ghost: ['bg-transparent text-v2-btn-secondary-text', 'hover:text-blue-500'].join(' '),
        icon: [
          'bg-transparent text-v2-icon-primary',
          'hover:bg-v2-btn-secondary hover:text-v2-icon-primary-hover',
        ].join(' '),
      },
      size: {
        L: 'h-11 px-3 py-2 text-[12px] font-normal leading-[16px] tracking-[-0.12px]',
        M: 'h-10 px-4 py-3 text-[13px] font-normal leading-[16px] tracking-[-0.13px]',
        S: 'px-3 py-2 text-[12px] font-normal leading-[16px] tracking-[-0.12px]',
      },
    },
    compoundVariants: [
      { appearance: 'icon', size: 'M', class: 'size-10 p-0' },
      { appearance: 'icon', size: 'S', class: 'size-8 p-0' },
      // Figma Button 124:59779: accent/neutral S set Legend/M (12px/115%) for 30px;
      // ghost/outline S keep 16px leading for 32.
      {
        appearance: ['accent', 'neutral'],
        size: 'S',
        class: 'h-[30px] leading-[1.15] tracking-normal',
      },
    ],
    defaultVariants: {
      appearance: 'accent',
      size: 'M',
    },
  },
);

interface ButtonProps extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

function Button({
  className,
  appearance,
  size,
  asChild = false,
  leftIcon,
  rightIcon,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  const iconSize = size === 'S' ? 'size-4' : 'size-5';
  const isIconOnly = appearance === 'icon';
  const iconOnlySize = size === 'S' ? 'size-4' : 'size-6';

  if (asChild) {
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ appearance, size }), className)}
        {...props}
      >
        {children}
      </Comp>
    );
  }

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ appearance, size }), className)}
      {...props}
    >
      {isIconOnly ? (
        <span className={cn('flex shrink-0 items-center justify-center', iconOnlySize)}>
          {children}
        </span>
      ) : (
        <>
          {leftIcon && <span className={cn('shrink-0', iconSize)}>{leftIcon}</span>}
          {children}
          {rightIcon && <span className={cn('shrink-0', iconSize)}>{rightIcon}</span>}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
